import { useEffect, useMemo, useState } from 'react';
import { Stack } from '@cowbell/ui/core';
import { MessageProcessor } from '@a2ui/web_core/v0_9';
import {
  A2uiSurface,
  type ReactComponentImplementation,
} from '@a2ui/react/v0_9';
import { useMooConfig } from '../../provider/internal-context';
import type { A2UIEnvelope } from '../../meta/types';
import type { A2UIClientAction } from '../../types/config';
import { useAppendAssistantTurn } from '../ChatPanel/chat-append-assistant-turn-context';
import { useChatSend } from '../ChatPanel/chat-send-context';
import { peekLiveDataModel, registerProcessor } from './liveProcessors';
import { SentEmailSummary } from './SentEmailSummary';

/** Per-bubble A2UI renderer. Owns one `MessageProcessor` (keyed on
 *  `messageId` so each assistant turn gets a fresh model) and renders one
 *  `<A2uiSurface>` per surface that the processed envelopes have created.
 *
 *  Lifecycle:
 *    - Envelopes arrive as an append-only array. The effect processes only
 *      the tail past `processedCount` and bumps the count to trigger a
 *      re-render so the new surface list flushes.
 *    - `processor.model.surfacesMap` is the source of truth for which
 *      surfaces are live; reading it on every render means `deleteSurface`
 *      lifecycle is implicit (no need to track removed ids by hand).
 *    - Actions emitted by the rendered surfaces are wrapped with
 *      `correlationId` (from the message meta), `messageId`, and
 *      `surfaceType` (the root component name) so the host's
 *      `onA2UIAction` has everything it needs without reaching back into
 *      shell state.
 *
 *  Streaming gate: the component renders nothing until `streamingComplete`.
 *  Half-streamed surfaces can still mutate, and the editable form mode
 *  ("Disabled-vs-invisible during streaming" in the design spec) only
 *  appears after `data-done`. */
export interface A2UISurfaceListProps {
  readonly messageId: string;
  /** Correlation id from the assistant message's `data-done`. May be `null`
   *  if the message hasn't finalized yet — paired with `streamingComplete`,
   *  this means actions can't fire until both are present. */
  readonly correlationId: string | null;
  readonly envelopes: A2UIEnvelope[];
  readonly streamingComplete: boolean;
  /** False once this message is no longer the latest turn. Chat-settled cards
   *  (e.g. AskQuestions) freeze when this is false so a stale card can't
   *  be re-answered — the same rule the V1 QuickReply card follows. Defaults to
   *  interactive. */
  readonly isLastMessage?: boolean;
}

export function A2UISurfaceList({
  messageId,
  correlationId,
  envelopes,
  streamingComplete,
  isLastMessage = true,
}: A2UISurfaceListProps) {
  const { a2uiCatalogs, onA2UIAction, logger } = useMooConfig();
  const appendAssistantTurn = useAppendAssistantTurn();
  const chatSend = useChatSend();
  // How many of `allEnvelopes` have been fed into WHICH processor. Tracking the
  // processor identity alongside the count (instead of a bare count + a
  // separate reset effect) closes a React-batching race: when a late
  // correlationId rebuilds the processor in the same commit as new
  // actionEnvelopes, a single effect keyed on identity always replays from 0
  // onto the fresh processor rather than slicing only the action tail onto it.
  const [processed, setProcessed] = useState<{
    processor: MessageProcessor<ReactComponentImplementation> | null;
    count: number;
  }>({ processor: null, count: 0 });
  // Surfaces that have completed a terminal Send. Swapped to a read-only
  // summary so the user can't double-send. Tracked locally because the
  // action endpoint isn't on the SSE stream; persisting this across reloads
  // is out of scope for the current slice (composer reverts to editable
  // after refresh — acceptable for now).
  const [sentSurfaceIds, setSentSurfaceIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  // Surfaces the user submitted (vs. left unanswered). Kept in React state, not
  // the data model, so it survives a processor rebuild that wipes in-model flags
  // — then re-asserted as `/submitted` so a settled card reads submitted vs
  // skipped. Same durability as the sent-email tracking above.
  const [submittedSurfaceIds, setSubmittedSurfaceIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  // A2UI envelopes returned by an action (off the SSE stream) that mutate/spawn
  // surfaces — the optimistic, in-session counterpart to the BE persisting the
  // same envelopes onto the stored message for reload. Appended after the meta
  // `envelopes` (an action always post-dates the surface it targets), and
  // replayed alongside them when the processor is rebuilt.
  const [actionEnvelopes, setActionEnvelopes] = useState<A2UIEnvelope[]>([]);

  // Lock in the dispatch id at the moment the stream finalizes (correlationId
  // arrives via data-done). The bubble passes `meta.serverMessageId ?? messageId`,
  // which flips client-id → server-id when data-done lands — both arrive together,
  // so capturing here records the server id. Holding it stable afterwards
  // prevents any later prop-level recomputation from recreating the processor
  // and wiping in-progress composer edits.
  //
  // Bugbot flags the theoretical "messageId changes after freeze → BE gets
  // stale id." In our codepath that cannot happen: serverMessageId is only
  // set in build-assistant-message-meta.ts::handleDone (the same handler
  // that sets correlationId), and history-replay payloads arrive with both
  // present from the first render. If a future stream path could update
  // serverMessageId independently of correlationId, this freeze would need
  // a one-shot useState capture instead — keeping the freeze for now to
  // preserve mid-edit composer state, which is the user-visible regression.
  const streamFinalized = correlationId !== null;
  const dispatchMessageId = useMemo(
    () => messageId,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: freeze
    // the dispatch id once the stream finalizes; later messageId updates must not
    // rebuild the processor (would wipe composer edits).
    [streamFinalized]
  );

  // Freeze `chatSend` the same way, and for the same reason: `useChatSend`
  // returns ChatPanel's `handleSubmit`, whose identity changes whenever an
  // upload completes, merged contexts change, the tone switches, or the
  // conversation id lands. Listing the live value on the processor `useMemo`
  // would rebuild the processor on any of those, wiping the in-progress
  // `/answers` picks and the optimistic `/locked` write (only `/submitted`
  // survives, re-asserted from React state) — a half-answered card would reset
  // its selections or go dead on Done. Capturing at finalize is safe: ask cards
  // only become interactive after the stream completes, and the frozen closure
  // still enqueues an ordinary follow-up user turn with a stable agent/
  // conversation id.
  const dispatchChatSend = useMemo(
    () => chatSend,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional freeze; see above.
    [streamFinalized]
  );

  // The action handler closure reads `processor` from its own enclosing
  // useMemo. The arrow only runs when a surface dispatches an action, by
  // which point `processor` is fully initialized — no ref needed (project
  // memory: refs are for DOM nodes only). Resolves OQ-1.
  //
  // The deps list is wider than the plan's `[messageId, a2uiCatalogs,
  // handleAction]` because we inlined the handler. The host must supply
  // stable references for `onA2UIAction`, `logger`, and `a2uiCatalogs`
  // (T14 wires this through `useCallback` / module-level catalog arrays).
  const processor = useMemo(() => {
    const p: MessageProcessor<ReactComponentImplementation> =
      new MessageProcessor<ReactComponentImplementation>(
        a2uiCatalogs ?? [],
        (raw) => {
          const surfaceType = readSurfaceType(p, raw.surfaceId);
          const augmented: A2UIClientAction = {
            name: raw.name,
            surfaceId: raw.surfaceId,
            surfaceType,
            sourceComponentId: raw.sourceComponentId ?? '',
            correlationId: correlationId ?? '',
            messageId: dispatchMessageId,
            payload: raw.context,
          };
          // Answered by sending a chat message, not by calling the action
          // endpoint (see CHAT_SEND_ACTIONS). Checked before `onA2UIAction`
          // because this path never needs one — the answer re-enters the graph
          // as an ordinary user turn.
          if (CHAT_SEND_ACTIONS.has(`${surfaceType}:${raw.name}`)) {
            if (!dispatchChatSend) {
              logger.debug('moo.a2ui.action.no_chat_send', { ...augmented });
              return;
            }
            // Drop a duplicate submit (e.g. a double-click on Done before the
            // re-render disables it): the first dispatch locks the surface
            // synchronously, so a second in the same tick reads it and bails —
            // the equivalent of the `/_inFlight` mutex the endpoint path uses.
            if (isSurfaceLocked(p, raw.surfaceId)) {
              logger.debug('moo.a2ui.action.chat_send_dropped', {
                ...augmented,
              });
              return;
            }
            const text = formatAnswersMessage(raw.context);
            if (text) {
              // Freeze and mark submitted synchronously so the card settles this
              // tick, not when the next turn finalizes (this path never settles
              // `/results`). The state below is the durable copy; this write is
              // what the first settled render reads, before the effect runs.
              lockSurface(p, raw.surfaceId);
              markSubmitted(p, raw.surfaceId);
              const submittedId = raw.surfaceId;
              setSubmittedSurfaceIds((prev) =>
                prev.has(submittedId) ? prev : new Set(prev).add(submittedId)
              );
              dispatchChatSend(text);
            }
            return;
          }
          if (onA2UIAction) {
            // In-flight mutex: write a sentinel directly into the A2UI data
            // model (synchronous, accessible from this stable closure, no
            // React-state staleness). Rapid duplicate clicks read the sentinel
            // and bail before dispatching a second POST. We use the data model
            // rather than React state because the processor closure captures
            // state at creation time — refs would solve this too but are
            // banned project-wide for non-DOM use.
            //
            // Keyed by the dispatching component's own id, not the surface —
            // a CopyQuote surface has one QuoteOption child per option, each
            // firing its own GenerateQuoteOption independently. A single
            // surface-wide flag serialized every row behind whichever one
            // clicked first, silently dropping a second row's click while
            // the first was still in flight.
            const surface = p.model.getSurface(raw.surfaceId);
            if (!surface) return;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI runtime types
            const dm = surface.dataModel as any;
            const inFlightKey = `/_inFlight/${raw.sourceComponentId || 'root'}`;
            if (dm.get?.(inFlightKey) === true) {
              logger.debug('moo.a2ui.action.in_flight_dropped', {
                surfaceId: augmented.surfaceId,
                action: augmented.name,
              });
              return;
            }
            dm.set?.(inFlightKey, true);
            // Surface the host's result message as a synthetic assistant turn
            // (the action endpoint is off-stream, so silent success has no
            // visible feedback in the chat without this).
            void applyActionResult({
              onA2UIAction,
              augmented,
              dm,
              appendAssistantTurn,
              setActionEnvelopes,
              setSentSurfaceIds,
            }).finally(() => dm.set?.(inFlightKey, false));
          } else {
            logger.debug('moo.a2ui.action.no_handler', { ...augmented });
          }
        }
      );
    return p;
  }, [
    dispatchMessageId,
    correlationId,
    a2uiCatalogs,
    onA2UIAction,
    logger,
    appendAssistantTurn,
    dispatchChatSend,
  ]);

  // The meta stream plus any action-returned envelopes, in causal order (an
  // action always post-dates the surface it targets). Processed as one list so
  // a processor rebuild replays the action mutations too, not just the original
  // stream.
  const allEnvelopes = useMemo(
    () => [...envelopes, ...actionEnvelopes],
    [envelopes, actionEnvelopes]
  );

  useEffect(() => {
    // A new processor (e.g. correlationId arrived late) starts empty, so replay
    // ALL envelopes onto it; otherwise only process the unprocessed tail.
    // Deriving the start from the tracked processor identity — not a separate
    // reset effect — means a same-commit rebuild+append can't advance the count
    // before the reset lands and skip the base createSurface/updateComponents.
    const sameProcessor = processed.processor === processor;
    const start = sameProcessor ? processed.count : 0;
    if (allEnvelopes.length > start) {
      const tail = allEnvelopes.slice(start);
      try {
        // The processor's `processMessages` expects A2uiMessage[]; envelopes
        // are intentionally opaque at our boundary. Validation happens
        // inside the processor.
        processor.processMessages(tail as never);
      } catch (err) {
        logger.error('moo.a2ui.process_failed', {
          error: err instanceof Error ? err.message : String(err),
          messageId,
        });
      }
    }
    // Always advance past a (possibly failed) tail so a bad batch is logged
    // once, not re-run every render.
    if (!sameProcessor || processed.count !== allEnvelopes.length) {
      setProcessed({ processor, count: allEnvelopes.length });
    }
  }, [allEnvelopes, processed, processor, logger, messageId]);

  // Freeze every card once the turn is no longer the latest. Cards that don't
  // read `/locked` ignore it.
  useEffect(() => {
    if (isLastMessage) return;
    Array.from(processor.model.surfacesMap.keys()).forEach((id) =>
      lockSurface(processor, id)
    );
  }, [isLastMessage, processor, processed]);

  // Re-write `/submitted` from the durable set — a processor rebuild spawns a
  // fresh data model that has lost it.
  useEffect(() => {
    submittedSurfaceIds.forEach((id) => markSubmitted(processor, id));
  }, [submittedSurfaceIds, processor, processed]);

  // Published so a settling action can reach the model that is on screen NOW
  // rather than the one captured when it dispatched — see `liveProcessors`.
  useEffect(() => registerProcessor(processor), [processor]);

  if (!streamingComplete) return null;

  const surfaceIds = Array.from(processor.model.surfacesMap.keys());
  if (surfaceIds.length === 0) return null;

  return (
    <Stack gap="xs" mt="xs">
      {surfaceIds.map((id) => {
        const surface = processor.model.getSurface(id);
        if (!surface) return null;
        if (sentSurfaceIds.has(id)) {
          const data = (surface.dataModel.get('/') ?? {}) as Record<
            string,
            unknown
          >;
          return (
            <SentEmailSummary
              key={id}
              to={typeof data.to === 'string' ? data.to : ''}
              cc={typeof data.cc === 'string' ? data.cc : ''}
              bcc={typeof data.bcc === 'string' ? data.bcc : ''}
              subject={typeof data.subject === 'string' ? data.subject : ''}
              body={typeof data.body === 'string' ? data.body : ''}
            />
          );
        }
        return <A2uiSurface key={id} surface={surface} />;
      })}
    </Stack>
  );
}

/** (surfaceType, action name) pairs whose result settles into the dispatching
 *  component's own `/results/<id>` data-model path — the reactive channel a
 *  card polls to swap from its in-flight/preview state to a settled outcome.
 *  Add to this set, not the branching in `settleResultBoundAction`, when a
 *  new surface adopts the same result-binding contract. */
const RESULT_BOUND_ACTIONS: ReadonlySet<string> = new Set([
  'CopyQuote:GenerateQuoteOption',
  // A pending CreateQuoteRow's "Check status": the quote's current state lands
  // in its own `/results/<row id>` so the row can leave "Rating".
  'CreateQuote:RefreshQuoteStatus',
  'EditQuote:SaveQuoteEdit',
  // The hand-off card fires its action on MOUNT rather than from a click, so it
  // routinely settles while the assistant turn is still streaming. Without this
  // entry its only channel was `a2uiMessages` -> `setActionEnvelopes`, and that
  // state does not survive the re-render streaming completion causes: the write
  // was silently dropped and the card sat on "Staging on the workspace…" beside
  // a workspace that already held the changes. Measured in the browser — the
  // action resolved in 1.7s and `/results/root` was never written at all.
  'UnderwriterHandoff:StageViewChanges',
]);

/** (surfaceType, action name) pairs answered by sending a chat message instead
 *  of calling the action endpoint.
 *
 *  `ask_user` holds no graph interrupt — it can't, from inside the nested inner
 *  ReAct agent — so its answers have to re-enter the graph the only way they
 *  can: as an ordinary user turn. There is nothing to write server-side and
 *  nothing to settle into `/results`, which is why this branch runs before the
 *  `onA2UIAction` dispatch and returns without touching either. */
const CHAT_SEND_ACTIONS: ReadonlySet<string> = new Set([
  'AskQuestions:SubmitAnswers',
  // ClaimReport is a suggestions-only card, same design as AskQuestions: its
  // one action re-enters the graph as an ordinary chat turn rather than a
  // server write — there is nothing to settle into `/results`.
  'ClaimReport:SendClaimChoices',
]);

/** One answered question, as the agent emitted it alongside the answers map.
 *  `question` is carried in the action context rather than read back off the
 *  component so the message the model sees is verbatim what the card asked. */
interface AnsweredQuestion {
  readonly field?: string;
  readonly question?: string;
}

/** Render an answer, which is a string for a single-choice question and an
 *  array of labels for a multi-select one. */
function answerLine(value: unknown): string {
  if (Array.isArray(value)) return value.map((v) => String(v)).join(', ');
  if (value === null || value === undefined) return '';
  return String(value);
}

/** The follow-up user turn for a `CHAT_SEND_ACTIONS` dispatch: one
 *  `question answer` line per answered question, in the order asked.
 *
 *  Returns '' when there is nothing to send — no pick was recorded — so the
 *  card settles silently and the user's own next turn is the answer.
 *
 *  Keyed on question text rather than field name (matching the Agent SDK's
 *  `AskUserQuestion` answers contract) so the model reads back exactly what the
 *  user was shown, not an internal slug. */
export function formatAnswersMessage(context: unknown): string {
  if (!context || typeof context !== 'object') return '';
  const ctx = context as Record<string, unknown>;
  const answers = (ctx.answers ?? {}) as Record<string, unknown>;
  if (typeof answers !== 'object' || answers === null) return '';
  const asked = Array.isArray(ctx.questions)
    ? (ctx.questions as AnsweredQuestion[])
    : [];
  // Fall back to the answers map's own order when the emitter sent no question
  // list — a bare `field: value` line still beats sending nothing.
  const order: AnsweredQuestion[] =
    asked.length > 0 ? asked : Object.keys(answers).map((field) => ({ field }));
  const lines: string[] = [];
  for (const entry of order) {
    const field = entry?.field;
    if (!field) continue;
    const answer = answerLine(answers[field]);
    if (!answer) continue;
    lines.push(
      entry.question ? `${entry.question} ${answer}` : `${field}: ${answer}`
    );
  }
  return lines.join('\n');
}

/** Settles a result-bound action's `/results/<id>` path (see
 *  `RESULT_BOUND_ACTIONS`) so the dispatching component re-renders. A real
 *  result writes `data`; a genuine failure/malformed success writes an
 *  error-shaped value so the card doesn't hang in its in-flight state. An
 *  `ok` result whose success arrived via `a2uiMessages` (no `data`) is NOT an
 *  error and must not be overwritten with one. Extracted to keep the action
 *  handler's complexity in check. */
function settleResultBoundAction(params: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI runtime types
  readonly dm: any;
  readonly augmented: A2UIClientAction;
  readonly ok: boolean;
  readonly data?: Record<string, unknown> | null;
  readonly hasReturnedMessages: boolean;
  readonly message?: string;
}): void {
  const { dm, augmented, ok, data, hasReturnedMessages, message } = params;
  if (!RESULT_BOUND_ACTIONS.has(`${augmented.surfaceType}:${augmented.name}`)) {
    return;
  }
  // Same fallback the in-flight mutex above uses, and for the same reason: an
  // action fired on MOUNT rather than from a click arrives without a source
  // component id, so `sourceComponentId` lands as `''`.
  //
  // Bailing on that empty id silently skipped the whole settle. Measured in the
  // browser: `/_inFlight/root` toggled (it has this fallback) while
  // `/results/...` was never written at all, leaving a card bound to
  // `/results/root` reporting nothing over a workspace that held the changes.
  const componentId = augmented.sourceComponentId || 'root';
  const path = `/results/${componentId}`;
  // The model on screen NOW, not the one captured when the action dispatched.
  // See `liveProcessors` for what replaces it mid-flight and why the captured
  // one goes quiet rather than failing.
  const live = peekLiveDataModel(augmented.surfaceId) ?? dm;
  if (ok && data) {
    live.set?.(path, data);
    return;
  }
  if (!(ok && hasReturnedMessages)) {
    live.set?.(path, { error: true, message });
  }
}

/** Applies the host's action result off the fire-and-forget dispatch: surfaces
 *  the message as a synthetic assistant turn, feeds any returned envelopes into
 *  the processor (optimistic, in-session), locks a sent EmailComposer, and
 *  settles a result-bound action (see `RESULT_BOUND_ACTIONS`). Extracted from
 *  the processor's action callback so that callback stays shallow and under
 *  the cognitive-complexity budget. */
async function applyActionResult(params: {
  readonly onA2UIAction: (action: A2UIClientAction) => void | Promise<void | {
    readonly ok?: boolean;
    readonly message?: string;
    readonly data?: Record<string, unknown> | null;
    readonly a2uiMessages?: readonly A2UIEnvelope[];
  }>;
  readonly augmented: A2UIClientAction;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI runtime types
  readonly dm: any;
  readonly appendAssistantTurn: ((text: string) => void) | null;
  readonly setActionEnvelopes: (
    update: (prev: A2UIEnvelope[]) => A2UIEnvelope[]
  ) => void;
  readonly setSentSurfaceIds: (
    update: (prev: ReadonlySet<string>) => ReadonlySet<string>
  ) => void;
}): Promise<void> {
  const {
    onA2UIAction,
    augmented,
    dm,
    appendAssistantTurn,
    setActionEnvelopes,
    setSentSurfaceIds,
  } = params;
  const settled = await onA2UIAction(augmented);
  const result = settled && typeof settled === 'object' ? settled : undefined;
  const ok = result?.ok === true;
  const message = result?.message;
  const returnedMessages = result?.a2uiMessages;
  const hasReturnedMessages =
    Array.isArray(returnedMessages) && returnedMessages.length > 0;

  if (message && appendAssistantTurn) {
    appendAssistantTurn(message);
  }
  // Feed action-returned envelopes into the processor (optimistic). Additive
  // to the `/results` write in settleResultBoundAction — a Generate returns
  // both the result `data` and the note envelopes.
  //
  // NOT gated on `ok`. Envelopes are what the host chose to render, not a
  // claim that the action succeeded: a refusal that returns one is reporting
  // the refusal ON the surface, which is the only place a card can retire its
  // own buttons. Suppressing those left a refused action looking untried —
  // its message in the thread, its card still offering to do the thing that
  // was just declined.
  if (hasReturnedMessages) {
    setActionEnvelopes((prev) => [
      ...prev,
      ...(returnedMessages as A2UIEnvelope[]),
    ]);
  }
  // Terminal Send → lock the surface into a read-only "Sent" view. Failures
  // leave the composer editable so the user can adjust and retry.
  if (
    ok &&
    augmented.surfaceType === 'EmailComposer' &&
    augmented.name === 'Send'
  ) {
    setSentSurfaceIds((prev) => {
      if (prev.has(augmented.surfaceId)) return prev;
      const next = new Set(prev);
      next.add(augmented.surfaceId);
      return next;
    });
  }
  settleResultBoundAction({
    dm,
    augmented,
    ok,
    data: result?.data,
    hasReturnedMessages,
    message,
  });
}

/** Set a boolean flag on a surface's data model — the reactive channel
 *  chat-settled cards (e.g. AskQuestions) poll to change how they render.
 *  Idempotent: skips the write when the flag is already true. */
function setSurfaceFlag(
  processor: MessageProcessor<ReactComponentImplementation>,
  surfaceId: string,
  path: '/locked' | '/submitted'
): void {
  const surface = processor.model.getSurface(surfaceId);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI runtime types
  const dm = surface?.dataModel as any;
  if (dm?.get?.(path) !== true) dm?.set?.(path, true);
}

/** Freeze a surface so a stale card can't be re-answered. Written both
 *  optimistically at submit and on the isLastMessage flip. */
function lockSurface(
  processor: MessageProcessor<ReactComponentImplementation>,
  surfaceId: string
): void {
  setSurfaceFlag(processor, surfaceId, '/locked');
}

/** Whether a surface is already frozen — used to drop a duplicate submit. */
function isSurfaceLocked(
  processor: MessageProcessor<ReactComponentImplementation>,
  surfaceId: string
): boolean {
  const surface = processor.model.getSurface(surfaceId);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI runtime types
  const dm = surface?.dataModel as any;
  return dm?.get?.('/locked') === true;
}

/** Mark a surface submitted so its settled card reads "submitted" rather than
 *  the "skipped" default — written at submit and re-asserted after a rebuild. */
function markSubmitted(
  processor: MessageProcessor<ReactComponentImplementation>,
  surfaceId: string
): void {
  setSurfaceFlag(processor, surfaceId, '/submitted');
}

/** Derives the surface root component name for action-context tagging. The
 *  convention is that the root component carries `id: 'root'` (matches the
 *  EmailComposer envelopes the BE emits and the cowbell/v1 catalog spec).
 *
 *  Two paths because the runtime exposes the component name in different
 *  ways depending on how the surface was built:
 *    - Real A2UI surfaces: `componentsModel.get('root').type`.
 *    - Test/inline-root surfaces (and the fixture used in tests): the
 *      create-surface payload puts `root.component` directly on the surface.
 *
 *  Returns '' when neither shape applies — `surfaceType` is informational on
 *  the action payload, not load-bearing. */
function readSurfaceType(
  processor: MessageProcessor<ReactComponentImplementation>,
  surfaceId: string
): string {
  const surface = processor.model.getSurface(surfaceId) as
    | (ReturnType<typeof processor.model.getSurface> & {
        componentsModel?: {
          get?: (id: string) => { type?: string } | undefined;
        };
        root?: { component?: string };
      })
    | undefined;
  if (!surface) return '';
  const fromModel = surface.componentsModel?.get?.('root')?.type;
  if (typeof fromModel === 'string' && fromModel.length > 0) return fromModel;
  const fromRoot = surface.root?.component;
  if (typeof fromRoot === 'string') return fromRoot;
  return '';
}
